"use client";

import { formatCurrency } from "@sa-nu/shared-utils/price";

import { ChevronRightIcon } from "../../../dx-icons";

export type FeeOptionCardData = {
  /** React key と data-testid (`fee-option-card-${key}`) を兼ねる識別子 */
  key: string;
  label: string;
  subLabel: string | null;
  discountLabel: string | null;
  /** 確定金額。取得中または未確定は null として扱い、loading フラグ側で判別する */
  price: number | null;
  loading: boolean;
  /** 未指定時は loading || price === null から導出する */
  disabled?: boolean;
  onClick: () => void;
};

export function FeeOptionCards({
  cards,
  scheduleLabel,
}: {
  cards: FeeOptionCardData[];
  /** カード群見出し。確定日程が無い間は非表示にするため null を許容する */
  scheduleLabel: string | null;
}) {
  return (
    // 高さ0の sticky アンカーから absolute で描画し、flow の高さを専有せず可視領域下部に固定する。
    <div className="sticky bottom-0 z-10 h-0">
      <div
        data-testid="fee-option-cards"
        // 条件付きレンダリングの初回マウント時にも再生されるよう、transition ではなく animation を使う。
        // 下端は画面端で押しづらくならないよう余白を厚くする。safe-area は Drawer Popup の pb が吸収済みなので足さない。
        className="bg-dx-surface-base animate-slide-up-in absolute right-0 bottom-0 left-0 flex flex-col gap-[8px] rounded-t-[16px] px-[16px] pt-[16px] pb-[32px] shadow-[0px_4px_6px_rgba(0,0,0,0.1),0px_0px_0px_rgba(0,0,0,0.02)]"
      >
        {scheduleLabel !== null && (
          <p className="dx-body-1-bold text-dx-text-default">{scheduleLabel}</p>
        )}
        {/* subLabel の有無で1行/2行のカードが混在するため、auto-rows-fr で背の高い方に揃える。 */}
        <div className="grid auto-rows-fr gap-[8px]">
          {cards.map(
            ({
              key,
              label,
              subLabel,
              discountLabel,
              price,
              loading,
              disabled,
              onClick,
            }) => (
              <button
                data-analytics-id="room_type_reserve_selected_fee_option"
                key={key}
                type="button"
                data-testid={`fee-option-card-${key}`}
                disabled={disabled ?? (loading || price === null)}
                onClick={onClick}
                className="border-dx-border-default bg-dx-surface-raised hover:bg-dx-bg-base-hover flex items-center gap-[12px] rounded-[12px] border py-[13px] pr-[14px] pl-[16px] text-left shadow-[0_0_0_1px_rgba(0,0,0,0.02),0_2px_6px_0_rgba(0,0,0,0.1)] transition-colors duration-150 disabled:opacity-50"
              >
                <span className="flex flex-1 flex-col items-start">
                  <span className="dx-body-2-bold text-dx-text-default">
                    {label}
                  </span>
                  {subLabel !== null && (
                    <span className="dx-body-3 text-dx-text-sub">
                      {subLabel}
                    </span>
                  )}
                </span>
                <span className="flex items-center gap-[8px]">
                  {discountLabel !== null && (
                    <span className="bg-dx-button-primary-fill text-dx-text-inverse dx-body-3 rounded-[3px] px-[4px] py-[2px]">
                      {discountLabel}
                    </span>
                  )}
                  <span className="font-roboto text-dx-text-default text-[16px] leading-[24px]">
                    {loading
                      ? "…"
                      : price !== null
                        ? formatCurrency(price)
                        : "-"}
                  </span>
                </span>
                <ChevronRightIcon className="text-dx-text-default size-[16px] shrink-0" />
              </button>
            ),
          )}
        </div>
      </div>
    </div>
  );
}
